/* eslint-disable no-underscore-dangle */
import classNames from 'clsx';
import * as d3 from 'd3';
import { round } from 'lodash';
import Moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MomentPropTypes from 'react-moment-proptypes';

/**
  This is the description of the DataUsedChart component.
  TODO: Should this component be renamed to a more general BarChart?
*/
export default class DataUsedChart extends Component {
  static toByteString = (num, roundToDecimal = 2) => {
    let byteValue = '';
    let unit = '';
    const amount = Number(num);
    if (amount < 1000) {
      byteValue = round(amount * 1, roundToDecimal);
      unit = 'B';
    } else if (amount >= 1000 && amount < 1000000) {
      byteValue = round(amount / 1000, roundToDecimal);
      unit = 'KB';
    } else if (amount >= 1000000 && amount < 1000000000) {
      byteValue = round(amount / 1000000, roundToDecimal);
      unit = 'MB';
    } else if (amount >= 1000000000) {
      byteValue = round(amount / 1000000000, roundToDecimal);
      unit = 'GB';
    }
    return `${byteValue} ${unit}`;
  };

  static getYAxisValues = (minValue, values) => {
    const maxValue = Math.max(...values);
    const valueDifference = maxValue - minValue;
    const basePower = `${parseInt(valueDifference / 10, 10)}`.length;
    const base = 10 ** basePower;
    const yValues = [minValue];
    let currentMultiplier = 1;

    while (currentMultiplier * base < maxValue + base) {
      yValues.push(currentMultiplier * base);
      currentMultiplier += 1;
    }
    return yValues;
  };

  constructor(props) {
    super(props);
    this.chart = React.createRef();
  }

  componentDidMount() {
    if (this.isHourly()) this.buildHourlyChart();
    else this.buildDailyChart();
  }

  isHourly = () => {
    const { data, defaultToDaily } = this.props;
    return (data.length === 0 && !defaultToDaily) || (data.length > 0 && 'hourofusage' in data[0]);
  };

  buildDailyChart = () => {
    const {
      data,
      emptyMessageDaily,
      height,
      isAlerts,
      margin,
      showMaxDataCallout,
      timeFilter,
      useTimeFilter,
      width,
    } = this.props;
    const hasData = data.length;
    const timeZone = Moment.tz.guess();
    let chartData;

    const maxYValue = showMaxDataCallout
      ? Math.max(...Array.from(data, (d) => parseInt(d.datausage, 10)))
      : null;

    const DATE_TOOLTIP_FORMAT = 'h:mma, UTC';
    const DAY_TOOLTIP_FORMAT = 'MMM DD, YYYY';
    const XAXIS_DATE_FORMAT = 'MMM D';

    if (hasData) {
      chartData = data.map((d) => ({
        value: parseInt(d.datausage, 10),
        date: Moment.utc(d.dateofusage),
        maxValue: maxYValue,
      }));
    } else {
      chartData = [];
      for (let i = 6; i > -1; i -= 1) {
        chartData.push({
          value: '0',
          date: Moment().tz(timeZone).utc().subtract(i, 'day'),
        });
      }
    }

    if (useTimeFilter) {
      const dates = [];
      const startDate = Moment(timeFilter.startDate, 'x').startOf('day');
      const endDate = Moment(timeFilter.endDate, 'x').startOf('day');

      while (startDate.diff(endDate) < 0) {
        dates.push(startDate.clone());
        startDate.add(1, 'days');
      }
      dates.push(endDate);

      const newData = [];
      dates.forEach((date) => {
        const newDate = {
          date,
          value: '0',
        };
        chartData.forEach((datum) => {
          if (date.isSame(datum.date, 'day')) {
            newDate.value = datum.value;
          }
        });
        newData.push(newDate);
      });
      chartData = newData;
    }

    // YAxisValues
    const yAxisValues =
      hasData > 0
        ? DataUsedChart.getYAxisValues(
            0,
            chartData.map((d) => d.value)
          )
        : [150000000, 100000000, 50000000, 0];

    // Chart building
    const chart = d3
      .select(this.chart.current)
      // svg canvas
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Gradient for alerts chart
    const defs = chart.append('defs');

    const alertGradient = defs
      .append('linearGradient')
      .attr('id', 'alert-gradient')
      .attr('gradientTransform', 'rotate(90)');

    alertGradient.append('stop').attr('stop-color', '#40ffe7').attr('offset', '0%');

    alertGradient.append('stop').attr('stop-color', '#41f9e8').attr('offset', '6%');

    alertGradient.append('stop').attr('stop-color', '#42f2ea').attr('offset', '12%');

    alertGradient.append('stop').attr('stop-color', '#43eaec').attr('offset', '19%');

    alertGradient.append('stop').attr('stop-color', '#44e2ee').attr('offset', '26%');

    alertGradient.append('stop').attr('stop-color', '#45d8ef').attr('offset', '34%');

    alertGradient.append('stop').attr('stop-color', '#46cef1').attr('offset', '42%');

    alertGradient.append('stop').attr('stop-color', '#47c4f3').attr('offset', '50%');

    alertGradient.append('stop').attr('stop-color', '#48b9f5').attr('offset', '58%');

    alertGradient.append('stop').attr('stop-color', '#4aadf7').attr('offset', '66%');

    alertGradient.append('stop').attr('stop-color', '#4ba0f9').attr('offset', '74%');

    alertGradient.append('stop').attr('stop-color', '#4c94fb').attr('offset', '81%');

    alertGradient.append('stop').attr('stop-color', '#4c87fc').attr('offset', '88%');

    alertGradient.append('stop').attr('stop-color', '#4d79fe').attr('offset', '94%');

    alertGradient.append('stop').attr('stop-color', '#2F6AFF').attr('offset', '100%');

    // X axis ticks
    const xScale = d3
      .scaleBand()
      .domain(chartData.map((d) => d.date))
      .range([0, width])
      .paddingInner(0.9)
      .paddingOuter(0.45);

    const xAxis = d3
      .axisBottom(xScale)
      .tickFormat((d) => d.format(XAXIS_DATE_FORMAT))
      .tickSize(0)
      .tickPadding(15)
      .tickValues(
        xScale.domain().filter((_, i) => {
          const endIndex = xScale.domain().length - 1;
          return i === 0 || i === endIndex || i === endIndex / 2;
        })
      );

    chart
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis);

    // X axis grid lines
    const gridScale = d3.scaleLinear().domain([0, chartData.length]).range([0, width]);

    const xAxisGrid = d3
      .axisBottom(gridScale)
      .ticks(chartData.length)
      .tickSize(height)
      .tickFormat('');

    chart.append('g').attr('class', 'x-axis-grid inner-chart-container').call(xAxisGrid);

    // Y axis grid lines
    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(yAxisValues) + d3.deviation(yAxisValues)])
      .range([height, 0]);

    const yAxisGrid = d3
      .axisLeft(yScale)
      .ticks(yAxisValues.length - 1)
      .tickSize(-width)
      .tickFormat('');

    chart.append('g').attr('class', 'y-axis-grid').call(yAxisGrid);

    // Y axis ticks
    const yAxis = d3
      .axisLeft(yScale)
      .ticks(yAxisValues.length - 1)
      .tickFormat(DataUsedChart.toByteString)
      .tickSize(0);

    chart.append('g').attr('class', 'y-axis').call(yAxis);

    // Tooltip
    const tooltip = d3.select(this.chart.current).append('div').attr('class', 'tooltip');

    // Data join
    if (hasData) {
      chart
        .selectAll('rect')
        .data(chartData)
        .join('rect')
        .attr('x', (d) => xScale(d.date))
        .attr('y', (d) => yScale(d.value))
        .attr('height', (d) => height - yScale(parseInt(d.value, 10)))
        .style('rx', '3px')
        .style('ry', '3px')
        .attr('class', (d) => `bar ${d.maxValue === d.value && 'bar--maxValue'}`)
        .style('fill', () => {
          if (isAlerts) {
            return 'url(#alert-gradient)';
          }

          return undefined;
        })
        .on('mouseover', function onChartMouseOver(d) {
          // use es5 function to keep d3's binding of 'this'
          d3.select(this).style('fill', '#2401DA');

          // give time for the previous tooltip to process a close, then open a new one here.
          setTimeout(() => {
            tooltip.transition().duration(100).style('opacity', 1);
          }, 4);

          const hoveredBar = d3.select(this).node();

          const tooltipOffsetTop = height - hoveredBar.getBoundingClientRect().height;
          const tooltipOffsetLeft = margin.left + Number(hoveredBar.getAttribute('x')) - 36;

          tooltip
            .html(
              `<div>
              <div class="tooltip-date">
                <span>${d.target.__data__.date.format(DAY_TOOLTIP_FORMAT)}</span>
                <span>${d.target.__data__.date.format(DATE_TOOLTIP_FORMAT)}</span>
              </div>
              <hr>
              <div class="tooltip-data">${DataUsedChart.toByteString(d.target.__data__.value)}</div>
            </div>`
            )
            .style('left', `${tooltipOffsetLeft}px`)
            // minus 80 because that is the height of the tooltip
            // if it's below 0, then place tooltip  at the top of the grid
            // prevents the tooltip from bleeding into the margin
            .style('top', `${tooltipOffsetTop - 80 < 0 ? margin.top : tooltipOffsetTop - 30}px`)

            .on('mouseover', () => {
              tooltip.node().setAttribute('locked', 'true');
            })
            .on('mouseout', () => {
              tooltip.node().removeAttribute('locked');
            });
        })
        .on('mouseout', function onChartMouseOver() {
          // give time for the tooltip mouseout to remove the locked attribute if it needs to.
          if (isAlerts) {
            d3.select(this).style('fill', 'url(#alert-gradient)');
          } else {
            d3.select(this).style('fill', '#2F6AFF');
          }

          setTimeout(() => {
            const tooltipLocked = tooltip.node().getAttribute('locked') === 'true';

            if (!tooltipLocked) {
              tooltip.transition().duration(200).style('opacity', 0);
            }
          }, 2);
        });

      if (showMaxDataCallout) {
        chart
          .append('text')
          .attr('class', 'chart__maxDataCallout')
          .attr('x', width / 57)
          .attr('y', height / 12)
          .text(`Max data used: ${DataUsedChart.toByteString(maxYValue)}`);

        const maxDataBar = d3.select('.bar--maxValue').node();

        chart
          .append(() => {
            const iconContainer = document.createElement('div'); // eslint-disable-line no-undef
            iconContainer.innerHTML = `<svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        >
        <g fill="none" fillRule="evenodd" stroke="none" strokeWidth="1">
        <g transform="translate(-200 -466)">
            <g transform="translate(144 418)">
            <g fill="#FFFFFF" transform="translate(60 52)">
                <path d="M1.883 0h4.234a.5.5 0 01.429.757L4.429 4.285a.5.5 0 01-.858 0L1.454.757A.5.5 0 011.884 0z" />
                <rect
                width="8"
                height="1"
                x="0"
                y="7"
                rx="0.5"
                transform="matrix(1 0 0 -1 0 15)"
                />
            </g>
            </g>
        </g>
        </g>
    </svg>`.trim();
            return iconContainer.firstChild;
          })
          .attr('class', 'chart__maxDataIcon')
          .attr('x', Number(maxDataBar.getAttribute('x')) - 4)
          .attr('y', Number(maxDataBar.getAttribute('y')) - 12);
      }
    } else {
      chart
        .append('text')
        .attr('class', 'chart__message')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .text(emptyMessageDaily)
        .style('text-anchor', 'middle')
        .style('fill', () => {
          if (isAlerts) {
            return '#fafaff';
          }

          return undefined;
        });
    }
  };

  buildHourlyChart = () => {
    const { data, emptyMessageHourly, margin, height, width } = this.props;
    const isDataEmpty = data.length === 0;
    const timeZone = Moment.tz.guess();
    let chartData;

    const XAXIS_TIME_FORMAT = 'ha, UTC';
    const XAXIS_DATE_FORMAT = 'MMM D';
    const DATE_TOOLTIP_FORMAT = 'h:mma, UTC';
    const DAY_TOOLTIP_FORMAT = 'MMM DD, YYYY';

    if (isDataEmpty) {
      const currentDate = new Date().toLocaleDateString();
      const currentTime = new Date().getHours();
      const array = new Array(25).fill(0);

      chartData = array.map((currentValue, i) => ({
        value: 0,
        date: Moment(currentDate).hour(currentTime).subtract(i, 'hours').tz(timeZone),
      }));
    } else {
      chartData = data.map((d) => ({
        value: d.datausage,
        date: Moment(d.dateofusage).hour(d.hourofusage).tz(timeZone),
      }));
    }

    // YAxisValues
    const yAxisValues = isDataEmpty
      ? [150000000, 100000000, 50000000, 0]
      : DataUsedChart.getYAxisValues(
          0,
          chartData.map((d) => parseInt(d.value, 10))
        );

    // Chart building
    const chart = d3
      .select(this.chart.current)
      // svg canvas
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // X axis ticks
    const xScale = d3
      .scaleBand()
      .domain(chartData.map((d) => d.date))
      .range([0, width])
      .paddingInner(0.9)
      .paddingOuter(0.45);

    const xAxis = d3
      .axisBottom(xScale)
      .tickFormat(() => '')
      .tickSize(0)
      .tickPadding(10);

    chart
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .selectAll('g')
      // customize x axis tick marks so only certain ones show
      .append('svg:foreignObject')
      .attr('width', 50)
      .attr('height', 30)
      .attr('x', -10)
      .attr('y', 10)
      .append('xhtml:div')
      .html((d, i) => {
        if (i === 0 || i % 8 === 0 || i === chartData.length) {
          return `<div class='x-label-text'>
              <div>${d.format(XAXIS_TIME_FORMAT)}</div><div>${d.format(XAXIS_DATE_FORMAT)}</div>
            </div>`;
        }

        return undefined;
      });

    // X axis grid lines
    const gridScale = d3.scaleLinear().domain([0, chartData.length]).range([0, width]);

    const xAxisGrid = d3
      .axisBottom(gridScale)
      .ticks(chartData.length)
      .tickSize(height)
      .tickFormat('');

    chart.append('g').attr('class', 'x-axis-grid').call(xAxisGrid);

    // Y axis ticks
    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(yAxisValues)])
      .range([height, 0]);

    const yAxis = d3
      .axisLeft(yScale)
      .ticks(yAxisValues.length - 1)
      .tickFormat(DataUsedChart.toByteString)
      .tickSize(0);

    chart.append('g').attr('class', 'y-axis').call(yAxis);

    // Y axis grid lines
    const yAxisGrid = d3
      .axisLeft(yScale)
      .ticks(yAxisValues.length - 1)
      .tickSize(-width)
      .tickFormat('');

    chart.append('g').attr('class', 'y-axis-grid').call(yAxisGrid);

    // Tooltip
    const tooltip = d3.select(this.chart.current).append('div').attr('class', 'tooltip');

    // Data join
    if (!isDataEmpty) {
      chart
        .selectAll('rect')
        .data(chartData)
        .join('rect')
        .attr('x', (d) => xScale(d.date))
        .attr('y', (d) => yScale(d.value))
        .attr('height', (d) => height - yScale(parseInt(d.value, 10)))
        .style('rx', '3px')
        .style('ry', '3px')
        .attr('class', 'bar')
        .on('mouseover', function onChartMouseOver(d) {
          // use es5 function to keep d3's binding of 'this'

          // give time for the previous tooltip to process a close, then open a new one here.
          setTimeout(() => {
            tooltip.transition().duration(100).style('opacity', 1);
          }, 4);

          const hoveredBar = d3.select(this).node();

          const tooltipOffsetTop = height - hoveredBar.getBoundingClientRect().height;
          const tooltipOffsetLeft = hoveredBar.getBoundingClientRect().left - 36;

          tooltip
            .html(
              `<div>
              <div class="tooltip-date">
                <span>${d.target.__data__.date.format(DAY_TOOLTIP_FORMAT)}</span>
                <span>${d.target.__data__.date.format(DATE_TOOLTIP_FORMAT)}</span>
              </div>
              <hr>
              <div class="tooltip-data">${DataUsedChart.toByteString(d.target.__data__.value)}</div>
            </div>`
            )
            .style('left', `${tooltipOffsetLeft}px`)
            // minus 80 because that is the height of the tooltip
            // if it's below 0, then place tooltip at the top of the grid
            // prevents the tooltip from bleeding into the margin
            .style('top', `${tooltipOffsetTop - 80 < 0 ? margin.top : tooltipOffsetTop - 30}px`)
            .on('mouseover', () => {
              tooltip.node().setAttribute('locked', 'true');
            })
            .on('mouseout', () => {
              tooltip.node().removeAttribute('locked');
            });
        })
        .on('mouseout', () => {
          // give time for the tooltip mouseout to remove the locked attribute if it needs to.

          setTimeout(() => {
            const tooltipLocked = tooltip.node().getAttribute('locked') === 'true';

            if (!tooltipLocked) {
              tooltip.transition().duration(200).style('opacity', 0);
            }
          }, 2);
        });
    } else {
      chart
        .append('text')
        .attr('class', 'chart__message')
        .attr('x', width / 2)
        .attr('y', height / 2)
        .text(emptyMessageHourly)
        .style('text-anchor', 'middle');
    }
  };

  render() {
    const { classes, width, margin } = this.props;
    const isHourly = this.isHourly();
    return (
      <div
        className={classNames('DataUsedChartD3', classes, {
          hourly: isHourly,
          daily: !isHourly,
        })}
      >
        <div
          className="chart"
          ref={this.chart}
          style={{ width: `${width + margin.left + margin.right}px` }}
        />
      </div>
    );
  }
}

DataUsedChart.propTypes = {
  /**
    Toggle to build daily chart as default (instead of hourly chart)
  */
  defaultToDaily: PropTypes.bool,
  /**
    Text displayed in Daily chart when no data is rendered
  */
  emptyMessageDaily: PropTypes.string,
  /**
    Text displayed in Hourly chart when no data is rendered
  */
  emptyMessageHourly: PropTypes.string,
  /**
    Toggle to use timeFilter prop to show a specific range of dates on chart
  */
  useTimeFilter: PropTypes.bool,
  /**
    Object with a startTime and endTime keys
  */
  timeFilter: PropTypes.shape({
    startDate: MomentPropTypes.momentObj,
    endDate: MomentPropTypes.momentObj,
  }),
  /**
    Object with top, right, bottom, and left keys to indicate the margins from the svg element
  */
  margin: PropTypes.shape({
    top: PropTypes.string,
    right: PropTypes.string,
    bottom: PropTypes.string,
    left: PropTypes.string,
  }),
  /**
    Height in px of the data area (svg height is determined by adding height, margin.top and
    margin.bottom elements)
  */
  height: PropTypes.number,
  /**
    Width in px of the data area (svg width is determined by adding width, margin.left and
    margin.right elements)
  */
  width: PropTypes.number,
  /**
    Array of objects with datausage, dateofusage, numDevices keys (for daily), datausage,
    dateofusage, numDevices, hourofusage (for hourly)
  */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      datausage: PropTypes.number,
      dateofusage: MomentPropTypes.momentObj,
      numDevices: PropTypes.number,
      hourofusage: PropTypes.number,
    })
  ),
  /**
    Toggle to build the data used charts alerts (found on the overview page in dashboard)
  */
  isAlerts: PropTypes.bool,
  /**
    Display icon to highlight the largest bar/data point in the chart
  */
  showMaxDataCallout: PropTypes.bool,
  /**
   * Classes to add to the chart
   */
  classes: PropTypes.string,
};

DataUsedChart.defaultProps = {
  defaultToDaily: true,
  emptyMessageDaily: '',
  emptyMessageHourly: '',
  useTimeFilter: false,
  timeFilter: {},
  margin: { top: 50, right: 50, bottom: 50, left: 50 },
  height: 200,
  width: 600,
  data: [],
  isAlerts: false,
  showMaxDataCallout: false,
  classes: '',
};
